import { z } from "zod";
import { KEY_MODEL_DEFAULTS, KEY_PURPOSES, keyModelsOf, type KeyProvider, type KeyPurpose } from "./aiKey";

/** The models on a key, in two groups (AI settings round four, the founder's list of 2026-10-20; brief `docs/briefs/ai-settings-round-four.md`
 *  rules 1 and 2 and the decisions after the red team's review). "Advanced" asks two questions: which model writes and which does the quick
 *  work. The stored shape stays one model per kind of work; a choice writes a whole group, and the models call takes whole groups only. The
 *  options are the newest Sonnet and the newest Haiku we price ("newest" = the highest version in the model's name); a stored model of either
 *  family runs as that family's newest priced model (decision 1), on the card and in the worker alike, through `resolveKeyModels`. Pure. */

export const KEY_MODEL_GROUPS = {
  writing: ["draft", "instructions", "suggestion", "investigate"],
  quick: ["classify", "site", "search", "scout"],
} as const satisfies Record<string, readonly KeyPurpose[]>;
export type KeyGroup = keyof typeof KEY_MODEL_GROUPS;
export const KEY_GROUPS = Object.keys(KEY_MODEL_GROUPS) as KeyGroup[];
export const KEY_GROUP_LABELS: Record<KeyGroup, string> = { writing: "Writing", quick: "Quick work" };
/** the model a group's select shows (its first kind of work's: drafts, classification) and whether its stored kinds disagree */
export type KeyGroupView = Record<KeyGroup, { model: string; mixed: boolean }>;

export function keyModelGroups(models: Record<string, string>): KeyGroupView {
  const view = (g: KeyGroup) => {
    const ids = KEY_MODEL_GROUPS[g].map((p) => models[p]);
    return { model: ids[0]!, mixed: new Set(ids).size > 1 };
  };
  return { writing: view("writing"), quick: view("quick") };
}
/** the body of one group choice: the group's four kinds of work on one model */
export const keyGroupPatch = (group: KeyGroup, model: string): Partial<Record<KeyPurpose, string>> => Object.fromEntries(KEY_MODEL_GROUPS[group].map((p) => [p, model]));

/** what a models call may name: whole groups, each on one model (report item 2: a call can never split a group the card shows as one) */
function wholeGroups(models: Partial<Record<KeyPurpose, string>>, ctx: z.RefinementCtx): void {
  const named = KEY_GROUPS.filter((g) => KEY_MODEL_GROUPS[g].some((p) => models[p] !== undefined));
  if (named.length === 0) ctx.addIssue({ code: "custom", message: "Choose a model for Writing or Quick work", path: ["models"] });
  for (const g of named) {
    const ids = KEY_MODEL_GROUPS[g].map((p) => models[p]);
    if (ids.some((m) => m === undefined)) ctx.addIssue({ code: "custom", message: `${KEY_GROUP_LABELS[g]} is chosen as a whole: name all of ${KEY_MODEL_GROUPS[g].join(", ")}`, path: ["models"] });
    else if (new Set(ids).size > 1) ctx.addIssue({ code: "custom", message: `${KEY_GROUP_LABELS[g]} runs on one model`, path: ["models"] });
  }
}
export const AiKeyModelsPatchSchema = z.object({ models: z.partialRecord(z.enum(KEY_PURPOSES), z.string().min(1).max(100)) }).strict().superRefine((b, ctx) => wholeGroups(b.models, ctx));

/* ---------- the families and their versions, per provider (round six: Gemini beside Claude; item 22: maps, so OpenAI follows) ---------- */
/** the two families a key of each provider may choose from (report item 1: a priced Opus adds no option; Flash-Lite is no Flash) */
export const KEY_FAMILIES_BY_PROVIDER = { anthropic: ["sonnet", "haiku"], gemini: ["pro", "flash"], openai: ["sol", "luna"] } as const satisfies Record<KeyProvider, readonly string[]>;
export type KeyFamily = (typeof KEY_FAMILIES_BY_PROVIDER)[KeyProvider][number];
/** each provider's model for the two groups: the bigger one writes, the quick one sorts and searches (Gemini: Pro writes, Flash is quick) */
const GROUP_DEFAULTS: Record<KeyProvider, Record<KeyGroup, string>> = {
  anthropic: { writing: KEY_MODEL_DEFAULTS.draft, quick: KEY_MODEL_DEFAULTS.classify },
  gemini: { writing: "gemini-3.1-pro-preview", quick: "gemini-3.5-flash" },
  // round seven (2026-10-22; research `docs/research/openai-keys.md`, the red team's item 1): Sol writes, Luna is quick; Astra is never offered
  openai: { writing: "gpt-6.1-sol", quick: "gpt-6-luna" },
};
/** the provider's defaults per kind of work (Anthropic's are `KEY_MODEL_DEFAULTS`) */
export function keyModelDefaults(provider: KeyProvider): Record<KeyPurpose, string> {
  if (provider === "anthropic") return KEY_MODEL_DEFAULTS;
  return Object.fromEntries(KEY_GROUPS.flatMap((g) => KEY_MODEL_GROUPS[g].map((p) => [p, GROUP_DEFAULTS[provider][g]]))) as Record<KeyPurpose, string>;
}

/** "claude-sonnet-5-5" → family "sonnet", version [5, 5]; anything else null */
export function claudeModelOf(id: string): { family: string; version: number[] } | null {
  const m = /^claude-([a-z]+)-(\d+(?:-\d+)*)$/.exec(id);
  return m ? { family: m[1]!, version: m[2]!.split("-").map(Number) } : null;
}
/** "gemini-3.1-pro-preview" → Pro [3, 1], a preview; "gemini-3.5-flash" → Flash [3, 5]. Only `gemini-<version>-pro|flash`, with an optional
 *  `-preview` and Google's dated tail (`-preview-09-2026`, `-preview-05-20`) or a GA build number (`-001`), counts (item 10): Flash-Lite,
 *  image, speech and embedding models read null. */
export function geminiModelOf(id: string): { family: "pro" | "flash"; version: number[]; preview: boolean } | null {
  const m = /^gemini-(\d+(?:\.\d+)*)-(pro|flash)(?:-(\d{3})|(-preview(?:-\d{2}-\d{2,4})?))?$/.exec(id);
  return m ? { family: m[2] as "pro" | "flash", version: m[1]!.split(".").map(Number), preview: Boolean(m[4]) } : null;
}
/** "gpt-6.1-sol" → Sol [6, 1]; "gpt-6-luna-2026-09-22" → Luna [6], snapshot "2026-09-22" (round seven, the red team's item 1): only
 *  `gpt-<version>-sol|luna` with an optional dated tail counts; Astra, Terra, mini, nano, pro, codex and any longer tail read null */
export function openaiModelOf(id: string): { family: "sol" | "luna"; version: number[]; snapshot: string | null } | null {
  const m = /^gpt-(\d+(?:\.\d+)*)-(sol|luna)(?:-(\d{4}-\d{2}-\d{2}))?$/.exec(id);
  return m ? { family: m[2] as "sol" | "luna", version: m[1]!.split(".").map(Number), snapshot: m[3] ?? null } : null;
}
type KeyModel = { provider: KeyProvider; family: KeyFamily; version: number[]; preview: boolean; snapshot: string | null };
/** a key model's provider, family, version, preview mark and dated snapshot, or null for a model no key may choose */
export function keyModelOf(id: string): KeyModel | null {
  const c = claudeModelOf(id);
  if (c && (KEY_FAMILIES_BY_PROVIDER.anthropic as readonly string[]).includes(c.family)) return { provider: "anthropic", family: c.family as KeyFamily, version: c.version, preview: false, snapshot: null };
  const g = geminiModelOf(id);
  if (g) return { provider: "gemini", ...g, snapshot: null };
  const o = openaiModelOf(id);
  return o ? { provider: "openai", ...o, preview: false } : null;
}
/** the provider whose models a name belongs to, by its start (claude-…, gemini-…, gpt-…), whatever its family; null for anything else */
export const modelProviderOf = (id: string): KeyProvider | null => (/^claude-/.test(id) ? "anthropic" : /^gemini-/.test(id) ? "gemini" : /^gpt-/.test(id) ? "openai" : null);
/** the newer of two versions by number (5.10 over 5.9, 5.5 over 5): positive when `a` is newer */
export function compareVersions(a: readonly number[], b: readonly number[]): number {
  for (let i = 0; i < Math.max(a.length, b.length); i++) { const d = (a[i] ?? 0) - (b[i] ?? 0); if (d !== 0) return d; }
  return 0;
}
/** at one version: the undated alias beats a dated snapshot, the later snapshot the earlier (round seven); positive when `a` is newer */
const compareSnapshots = (a: string | null, b: string | null): number => (a === b ? 0 : a === null ? 1 : b === null ? -1 : a > b ? 1 : -1);
/** newer for the options: the higher version; at one version the GA model beats its preview (item 10), the alias its snapshot */
export function compareKeyModels(a: string, b: string): number {
  const x = keyModelOf(a), y = keyModelOf(b);
  if (!x || !y) return 0;
  return compareVersions(x.version, y.version) || Number(y.preview) - Number(x.preview) || compareSnapshots(x.snapshot, y.snapshot);
}
export const keyFamilyOf = (id: string): KeyFamily | null => keyModelOf(id)?.family ?? null;
const cap = (s: string) => `${s.charAt(0).toUpperCase()}${s.slice(1)}`;
/** a model's name as its maker writes it: "claude-sonnet-5-5" → "Claude Sonnet 5.5", "gemini-3.1-pro-preview" → "Gemini Pro 3.1",
 *  "gpt-6.1-sol" → "GPT-6.1 Sol" (an unknown id stays as it is) */
export function keyModelLabel(id: string): string {
  const o = openaiModelOf(id);
  if (o) return `GPT-${o.version.join(".")} ${cap(o.family)}`; // OpenAI writes the version before the tier: "GPT-6.1 Sol"
  const g = geminiModelOf(id);
  if (g) return `Gemini ${cap(g.family)} ${g.version.join(".")}`;
  const m = claudeModelOf(id);
  return m ? `Claude ${cap(m.family)} ${m.version.join(".")}` : id;
}

/** The one rule for which model runs on a key (decision 1, item 3a; round six: per provider): the stored choices over the provider's defaults,
 *  each mapped to the newest of its family among the options offered (the newest priced models). A stored model of another provider (Claude
 *  left over on a Gemini key, or the other way round) never survives: it becomes this provider's default (report item 5). A model of this
 *  provider whose family has no option stays. */
export function resolveKeyModels(stored: unknown, offered: readonly { id: string }[], provider: KeyProvider = "anthropic"): Record<KeyPurpose, string> {
  const newest = new Map(offered.map((o) => [keyFamilyOf(o.id), o.id] as const));
  const defaults = keyModelDefaults(provider);
  const models = keyModelsOf(stored, defaults);
  const own = (p: KeyPurpose) => (modelProviderOf(models[p]) === provider ? models[p] : defaults[p]);
  return Object.fromEntries(KEY_PURPOSES.map((p) => { const m = own(p); return [p, newest.get(keyFamilyOf(m)) ?? m]; })) as Record<KeyPurpose, string>;
}
